import React, { useRef, useEffect, useCallback } from 'react';
import { number, func, object, array, string, shape } from 'prop-types';

import Menu from '../Menu/Menu';
import Exhibitions from '../Exhibitions/Exhibitions';
import About from '../About/About';
import EventsWrapper from '../EventsWrapper/EventsWrapper';
import Header from '../Header/Header';
import LanguageSwitcher from '../LanguageSwitcher/LanguageSwitcher';
import CollectionList from '../_common/CollectionList/CollectionList';
import ArchiveExhibitionsList from '../ArchiveExhibitionsList/ArchiveExhibitionsList';
import { SECTIONS } from '../../constants';

import './Main.scss';

const Main = ({
  about,
  setIsSidebarOpen,
  activeSection,
  setActiveSection,
  currentExhibitions,
  upcomingExhibitions,
  archiveExhibitions,
  currentEvents,
  previousEvents,
  upcomingEvents,
  education,
  podcast,
  publication,
  language,
  setLanguage,
  staticText,
}) => {
  const ref = useRef();
  const archiveRef = useRef();
  const eventsRef = useRef();
  const podcastsRef = useRef();
  const publicationsRef = useRef();
  const educationRef = useRef();
  const aboutRef = useRef();
  const itemRefs = useRef({});

  const registerItemRef = useCallback((section, id, element) => {
    const key = `${section}-${id}`;
    if (element) {
      itemRefs.current[key] = element;
    } else {
      delete itemRefs.current[key];
    }
  }, []);

  useEffect(() => {
    if (!activeSection) return;

    const { section, id } = activeSection;

    if (section === SECTIONS.TOP) {
      ref.current.scrollTop = 0;
    } else {
      const sectionRefs = {
        [SECTIONS.ARCHIVE]: archiveRef,
        [SECTIONS.EVENTS]: eventsRef,
        [SECTIONS.PODCASTS]: podcastsRef,
        [SECTIONS.PUBLICATIONS]: publicationsRef,
        [SECTIONS.EDUCATION]: educationRef,
        [SECTIONS.ABOUT]: aboutRef,
      };
      const element =
        id != null
          ? itemRefs.current[`${section}-${id}`]
          : sectionRefs[section] && sectionRefs[section].current;
      if (element) {
        element.scrollIntoView();
      }
    }

    setActiveSection(null);
  }, [activeSection, setActiveSection]);

  return (
    <main className="main">
      <section className="main__menu-container">
        <Header setActiveSection={setActiveSection} />
        <Menu
          currentExhibitions={currentExhibitions}
          upcomingExhibitions={upcomingExhibitions}
          archiveExhibitions={archiveExhibitions}
          upcomingEvents={upcomingEvents}
          setActiveSection={setActiveSection}
          educationItems={education}
          podcastItems={podcast}
          publicationItems={publication}
          setIsSidebarOpen={setIsSidebarOpen}
          staticText={staticText}
        />
      </section>

      <div ref={ref} className="main__content-container">
        <LanguageSwitcher
          language={language}
          setLanguage={setLanguage}
          hideOnMobile="hideOnMobile"
        />

        <section className="main__exhibitions-list-container">
          <div>
            {!!currentExhibitions.length && (
              <>
                {currentExhibitions.map((exhibition) => (
                  <Exhibitions
                    key={exhibition.id}
                    itemRef={(el) =>
                      registerItemRef(SECTIONS.EXHIBITION, exhibition.id, el)
                    }
                    name={exhibition.name}
                    title={exhibition.title}
                    type={exhibition.type}
                    dateFrom={exhibition.dateFrom}
                    dateTo={exhibition.dateTo}
                    description={exhibition.description}
                    subHeader={exhibition.subHeader}
                    descriptionBlock2={exhibition.descriptionBlock2}
                    subDescription={exhibition.subDescription}
                    listOfPeople={exhibition.listOfPeople}
                    pictures={exhibition.pictures}
                    pdfFiles={exhibition.pdfFiles}
                    staticText={staticText}
                    links={exhibition.link}
                    language={exhibition.language}
                    galleryFiles={exhibition.galleryFiles}
                  />
                ))}
              </>
            )}

            {!!upcomingExhibitions.length && (
              <>
                <h1 className="main__exhibitions-title">
                  {staticText.sidebarUpcomming}
                </h1>
                {upcomingExhibitions.map((exhibition) => (
                  <Exhibitions
                    key={exhibition.id}
                    itemRef={(el) =>
                      registerItemRef(SECTIONS.EXHIBITION, exhibition.id, el)
                    }
                    name={exhibition.name}
                    title={exhibition.title}
                    type={exhibition.type}
                    dateFrom={exhibition.dateFrom}
                    dateTo={exhibition.dateTo}
                    description={exhibition.description}
                    subHeader={exhibition.subHeader}
                    descriptionBlock2={exhibition.descriptionBlock2}
                    subDescription={exhibition.subDescription}
                    listOfPeople={exhibition.listOfPeople}
                    pictures={exhibition.pictures}
                    pdfFiles={exhibition.pdfFiles}
                    staticText={staticText}
                    links={exhibition.link}
                    language={exhibition.language}
                    galleryFiles={exhibition.galleryFiles}
                  />
                ))}
              </>
            )}
          </div>

          {!!archiveExhibitions.length && (
            <ArchiveExhibitionsList
              ref={archiveRef}
              archiveExhibitions={archiveExhibitions}
              staticText={staticText}
              language={language}
            />
          )}

          <EventsWrapper
            ref={eventsRef}
            {...{
              currentEvents,
              upcomingEvents,
              previousEvents,
              registerItemRef,
              staticText,
            }}
          />

          <CollectionList
            ref={podcastsRef}
            title={staticText.podcastSectionTitle}
            items={podcast}
            staticText={staticText}
          />

          <CollectionList
            ref={publicationsRef}
            title={staticText.publicationSectionTitle}
            items={publication}
            staticText={staticText}
          />

          <CollectionList
            ref={educationRef}
            title={staticText.educationSectionTitle}
            items={education}
            staticText={staticText}
            isCollapsible={false}
          />

          <About ref={aboutRef} about={about} staticText={staticText} />
        </section>
      </div>
    </main>
  );
};

Main.propTypes = {
  about: object.isRequired,
  setIsSidebarOpen: func.isRequired,
  activeSection: shape({ section: string.isRequired, id: number }),
  setActiveSection: func.isRequired,
  currentExhibitions: array,
  currentEvents: array,
  previousEvents: array,
  archiveExhibitions: array,
  upcomingExhibitions: array,
  upcomingEvents: array,
  education: array,
  podcast: array,
  publication: array,
  language: string.isRequired,
  setLanguage: func.isRequired,
  staticText: object,
};

Main.defaultProps = {
  activeSection: null,
  currentExhibitions: [],
  archiveExhibitions: [],
  currentEvents: [],
  previousEvents: [],
  upcomingExhibitions: [],
  upcomingEvents: [],
  education: [],
  podcast: [],
  publication: [],
  staticText: {},
};

export default Main;
